// animation.js
// ------------------------------------------------------------
// The animation cue layer: animationAssign() hands each element's
// rotate / scale / trans / o2p / color / fade / ui / text / ext / draw / zoom cue
// to its handler, and the registry (window.oscillaAnimRegistry) records what
// runs, for the observer, sync, still(), a rewind and a project switch.
// ------------------------------------------------------------

// -----------------------------
// Imports
// -----------------------------
import { ensureAnimWrapper } from "./animShared.js";
import { _applyPendingSnapshotForUid } from "./animSync.js";
import { handleScaleCue } from "./scale.js";
import { handleRotateCue } from "./rotate.js";
import { handleO2PCue } from "./o2p.js";
import { handleColorCue } from "./color.js";
import { handleTransCue, resetTrans } from "./trans.js";
import { handleUICue } from "./ui.js";
import { handleExtCue } from "./ext.js";
import { handleDrawCue } from "./draw.js";
import { appCue } from "./appCues.js";
import { stop as stopSequence } from "./sequence.js";
import { parseCueToAST } from "../parser/parser.js";
import { splitCueId } from "../parser/cueUtils.js";
import { handleFadeCueFromAST, primeFadeTargetFromAST } from "./fade.js";
import { onReset } from "../system/teardown.js";

// Ensure global registries exist
window.oscillaAnimRegistry = window.oscillaAnimRegistry || {};

// --------------------------------------------------------------
// runningAnimations is a Map
// --------------------------------------------------------------
window.runningAnimations = window.runningAnimations instanceof Map
    ? window.runningAnimations
    : new Map();

// ------------------------------------------------------------
//  UID RESOLUTION HELPER
// ------------------------------------------------------------
export function resolveAnimationUid(el, kind, astArgs = []) {

    // user-provided uid(x)
    const uidArg = astArgs.find(a => a.key === "uid" || a.type === "uid");
    if (uidArg && uidArg.value) {
        return String(uidArg.value).trim();
    }

    // persisted on element
    if (el.dataset && el.dataset.animUid) return el.dataset.animUid;

    // fallback
    return `${kind}_${Math.random().toString(36).slice(2)}`;
}

// ------------------------------------------------------------
// animationAssign(svgRoot)
// ------------------------------------------------------------
export function animationAssign(svgRoot) {
    if (!svgRoot) return;

    // Collect unique elements from both legacy id-DSL and data-oscilla sources.
    // data-oscilla is checked first so we always parse from the canonical location.
    const seen = new Set();
    const candidates = [];

    // data-oscilla elements (browser-edited cues with plain ids)
    svgRoot.querySelectorAll("[data-oscilla]").forEach(el => {
        const dsl = el.getAttribute("data-oscilla")?.trim();
        if (dsl && dsl.includes("(") && !seen.has(el)) {
            seen.add(el);
            candidates.push({ el, dsl });
        }
    });

    // Legacy id-DSL elements not yet covered above
    svgRoot.querySelectorAll("[id*='(']").forEach(el => {
        if (!seen.has(el)) {
            // Strip Inkscape duplicate-id suffixes like "scale(...)-2" → "scale(...)"
            let dsl = el.id?.trim();
            if (dsl) {
                const stripped = dsl.replace(/\)-\d+$/, ')');
                if (stripped !== dsl) {
                    el.id = stripped;
                    dsl = stripped;
                }
                seen.add(el);
                candidates.push({ el, dsl });
            }
        }
    });

    candidates.forEach(({ el, dsl }) => assignElementCues(el, dsl));
}

// ------------------------------------------------------------
// animationAssignElement(el)
// ------------------------------------------------------------
// One element through the same pass: what the score's load would start
// (trig:auto, or any trig in page mode) starts now, and the rest is set up
// to wait for its playhead, click or object. The DSL editor's Save runs it.
export function animationAssignElement(el) {
    const data = el?.getAttribute?.("data-oscilla")?.trim();
    const id = el?.id?.trim();
    const dsl = data?.includes("(") ? data : id?.includes("(") ? id : null;
    if (dsl) assignElementCues(el, dsl);
}

function assignElementCues(el, dsl) {
    // SKIP NON-ANIMATION IDS.
    //
    // This scan takes every element whose id contains "(", so it also
    // meets constructs that are not cues at all. parseCueToAST logs
    // "[CueDSL] ❌ Parse errors" BEFORE it throws, so probing one of these
    // would print an error on every project load even though the failure
    // is caught a line later. rehearsal() is a mark, not a cue, and is
    // read by the rehearsal-mark system instead.
    if (/^(propagate|reuse|use|assignCues|button|rehearsal|notes)\s*\(/.test(dsl)) {
        return;
    }

    // Split compound IDs ("color(...) scale(...)") and process each cue
    const cueExprs = splitCueId(dsl);
    for (const cueExpr of (cueExprs.length ? cueExprs : [dsl])) {
        // drag() beside an animation: preProcessDrag's, not a cue
        if (/^drag\s*\(/.test(cueExpr)) continue;
        let ast = null;
        try {
            ast = parseCueToAST(cueExpr);
        } catch (err) {
            continue;
        }

        if (!ast || !ast.type) continue;

        switch (ast.type) {
            case "cueScale":
                handleScaleCue(ast, el);
                break;

            case "cueRotate":
                handleRotateCue(el, ast.args);
                break;

            case "cueTrans":
                handleTransCue(ast, el);
                break;

            case "cueO2P":
                handleO2PCue(el, ast.args);
                break;

            case "cueColor":
                handleColorCue(el, ast.args);
                break;

            case "cueFade": {
                const trigArg = ast.args?.find(a => a.type === "trig");
                const trigVal = String(trigArg?.value || "playhead").toLowerCase();
                if (trigVal === "auto") {
                    handleFadeCueFromAST(ast, el);
                } else {
                    primeFadeTargetFromAST(ast, el);
                }
                break;
            }

            case "cueUi": {
                // ui(…, trig:auto): set a starting state once the project has
                // finished loading — after the saved show/hide state is restored,
                // which would otherwise overrule it (e.g. hide a constellation
                // that a later cue reveals)
                const uiTrig = String(ast.args?.find(a => a.type === "trig")?.value || "").toLowerCase();
                // trig:manual / none — only another cue (next:) runs it
                if (uiTrig === "manual" || uiTrig === "none") break;
                if (uiTrig === "auto") {
                    const run = () => requestAnimationFrame(() => handleUICue(ast, el));
                    if (window._projectLoaded) run();
                    else window.addEventListener("oscilla:project-loaded", run, { once: true });
                    break;
                }
                // Only process ui() cues at load if they have signal bindings
                // Skip action:toggle, click:1 type cues - those respond to user interaction
                const hasAction = ast.args?.some(a => a.type === "action");
                const hasClick = ast.args?.some(a => a.type === "click" || a.type === "drag-click");
                if (!hasAction && !hasClick) {
                    handleUICue(ast, el);
                }
                break;
            }

            case "cueText": {
                // The element stays visible (and pickable in the DSL inspector)
                // unless the cue asks for hide:1.
                const hideArg = ast.args?.find(a => a.type === "hide");
                if ([true, "true", 1, "1"].includes(hideArg?.value)) {
                    el.style.opacity = "0";
                    el.style.visibility = "hidden";
                    el.style.pointerEvents = "none";
                }

                const isPageMode = window.mode === "page";

                const autostartFlag = ast.args?.some(a =>
                    a.type === "autostart" && String(a.value).trim() === "1"
                );

                if (isPageMode || autostartFlag) {
                    import("./text.js")
                        .then(mod => mod.handleCueTextFromAST(ast, el))
                        .catch(err => console.error("[animationAssign] cue:text autostart failed", err));
                }
                break;
            }

            case "cueExt":
                handleExtCue(ast, el);
                break;

            // draw(): a pen is a property of the object, not an event, so it
            // starts with the score unless the cue asks for a trigger. It has
            // to start HERE and not from the playhead walk: the motion it
            // records is usually trig:auto and already running by then, and a
            // pen that starts late has missed the line it existed to draw.
            case "cueDraw": {
                const trigArg = ast.args?.find(a => a.type === "trig");
                // a pen defaults to auto; a control (start:/stop:/clear:) is an
                // event, so it waits for the playhead unless it says auto
                const isControl = ast.args?.some(a => ["start", "stop", "clear"].includes(a.type));
                const trig = String(trigArg?.value || (isControl ? "playhead" : "auto")).toLowerCase();
                if (trig === "auto") handleDrawCue(ast, el);
                break;
            }

            // zoom(trig:auto): set the opening view. It has to wait until the
            // project has finished loading, or the saved-view restore undoes it.
            case "cueZoom": {
                const trigArg = ast.args?.find(a => a.type === "trig");
                if (String(trigArg?.value || "").toLowerCase() === "auto") {
                    // the score's (scoreCues.js); a page without one has no zoom
                    const run = () => requestAnimationFrame(() => requestAnimationFrame(() => appCue("cueZoom")?.(ast, el, {})));
                    if (window._projectLoaded) run();
                    else window.addEventListener("oscilla:project-loaded", run, { once: true });
                }
                break;
            }

            default:
                break;
        }
    } // end for cueExpr
}

/**
 * Stop one kind of animation on an element ("rotate", "scale", "trans",
 * "color"), leaving it where it has got to: rotate(off:1) from the live
 * console. Returns how many were stopped.
 */
const LOOP_ON_EL = { rotate: "_oscillaRotateAnim", scale: "_oscillaScaleAnim", color: "_oscillaColorAnim", o2p: "_o2pAnim" };

/**
 * Put an element's transforms back as authored: the element's own, the base
 * wrapper's and rotate's wrapper inside it (ensureAnimWrapper(el, "rotate")).
 */
function clearAnimTransforms(el) {
    el.style.transform = "";
    el.style.translate = "";
    const wrapper = el._oscillaAnimWrapper || el.querySelector?.(":scope > g.oscilla-anim");
    for (const w of [wrapper, el._oscillaAnimWrapper_rotate]) {
        if (w) { w.style.transform = ""; w.style.translate = ""; }
    }
}

export function stopAnimationsOn(el, kind) {
    if (!el) return 0;
    const registry = window.oscillaAnimRegistry || {};
    let n = 0;
    for (const uid of Object.keys(registry)) {
        const entry = registry[uid];
        const k = entry?.kind || entry?.cfg?.kind || "";
        // rotate is registered as rotate-fallback / rotate-sequence, color as color-hue ...
        if (!entry || entry.el !== el || (k !== kind && !k.startsWith(kind + "-"))) continue;
        const cfg = entry.cfg;
        if (cfg) cfg._stopped = true;
        try { cfg?._anim?.stop?.(); } catch { /* already stopped */ }
        try { cfg?._anim?.pause?.(); } catch { /* already stopped */ }
        try { window.runningAnimations?.get(uid)?.stop?.(); } catch { /* already stopped */ }
        window.runningAnimations?.delete(uid);
        delete registry[uid];
        n++;
    }
    // A continuous rotate, scale or colour runs on a loop kept on the
    // element -- stopped even when its registry entry is gone: entries are
    // keyed by uid, and a shape's rotate and o2p share the shape's.
    const loop = el[LOOP_ON_EL[kind]];
    if (loop?.stop) { try { loop.stop(); } catch { /* already stopped */ } n++; }
    if (kind === "trans") clearTimeout(el._oscillaTransTimer);
    return n;
}

/** still(): every animation stops where it has got to -- stopAnimationsOn, for all of them. */
export function stopAllAnimations() {
    stopSequence();   // pending next:/tdelay steps and a moving zoom()
    window.pendingCueStarts?.clear();   // their timers went with the sequence
    const registry = window.oscillaAnimRegistry || {};
    const els = new Set();
    for (const uid of Object.keys(registry)) {
        const cfg = registry[uid]?.cfg;
        if (cfg) cfg._stopped = true;
        try { cfg?._anim?.stop?.(); } catch { /* already stopped */ }
        try { cfg?._anim?.pause?.(); } catch { /* already stopped */ }
        try { window.runningAnimations?.get(uid)?.stop?.(); } catch { /* already stopped */ }
        window.runningAnimations?.delete(uid);
        if (registry[uid]?.el) els.add(registry[uid].el);
        delete registry[uid];
    }
    for (const el of els) {
        for (const k of Object.values(LOOP_ON_EL)) {
            try { el[k]?.stop?.(); } catch { /* already stopped */ }
        }
        clearTimeout(el._oscillaTransTimer);
    }
    return els.size;
}
onReset("stage", stopAllAnimations);
// ... and forgotten on a project switch, or the old score's keep ticking,
// broadcasting their positions and sending OSC (osc:1 runs offscreen)
onReset("project", function forgetAnimations() { window.runningAnimations.clear(); });

// ------------------------------------------------------------
// Reset all animations to their authored start positions.
// Stops runs and clears the transforms the engines apply
// (transform for rotate/scale wrappers, CSS translate for trans).
// ------------------------------------------------------------
/**
 * Stop and reset the animations one element started, and forget that they ran.
 *
 * Used when the playhead is scrubbed back to BEFORE a trig:playhead animation:
 * the score should look as it did before that object played, and the cue should
 * fire cleanly on the next forward pass. Without this a rotate() would carry
 * on spinning behind a playhead that had not reached it yet.
 *
 * Only touches what this element owns. A drone or an autostarted animation
 * elsewhere is not disturbed.
 */
export function resetAnimationsForElement(el) {
    if (!el) return 0;
    const registry = window.oscillaAnimRegistry || {};
    let n = 0;
    for (const uid of Object.keys(registry)) {
        const entry = registry[uid];
        if (!entry || entry.el !== el) continue;
        const cfg = entry.cfg;
        try { cfg?._anim?.stop?.(); } catch { /* ignore */ }
        try { cfg?._anim?.pause?.(); } catch { /* already stopped */ }
        try { window.runningAnimations?.get(uid)?.stop?.(); } catch { /* ignore */ }
        if (cfg?.kind === "trans" || entry.kind === "trans") {
            try { resetTrans(el, cfg || {}); } catch { /* ignore */ }
        }
        try { cfg?._reset?.(); } catch { /* ignore */ }
        for (const t of ["_oscillaRotateAnim", "_oscillaScaleAnim", "_oscillaTransTimer"]) {
            try { clearTimeout(el[t]); } catch { /* ignore */ }
        }
        try { clearAnimTransforms(el); } catch { /* ignore */ }
        entry._observerPaused = false;
        entry.started = false;
        // Retired: nothing may start this run again — in particular the
        // visibility observer, which resumes what it paused when the element
        // comes back on screen. Cleared when a fresh run begins.
        if (cfg) { cfg._armState = "idle"; cfg._stopped = true; }
        window.runningAnimations?.delete(uid);
        n++;
    }
    return n;
}

/**
 * Is this element actually mid-animation? Synchronous and cheap, so the cue
 * loop can ask every frame.
 *
 * The scrub-back check cannot use window.triggeredCues to mean "has fired":
 * every move of the playhead resets it (resetCueState), so by the time the
 * cue loop sees the playhead behind the object the set is already empty and
 * the object keeps spinning. Running state lives
 * here instead, where clearing it is not somebody else's job.
 */
//
// Answered from an index by element, not by walking the registry: the cue
// loop asks this for every playhead-triggered cue on every frame, and the
// registry holds every animation, so a walk would be cues x animations per
// frame. Entries are deleted from the registry
// in several places without telling anyone, so the index is checked against
// the registry on the way through and pruned where it has gone stale.
const _animsByEl = new WeakMap();   // el -> Set<uid>

function _indexAnimation(el, uid) {
    if (!el || typeof el !== "object") return;
    let set = _animsByEl.get(el);
    if (!set) { set = new Set(); _animsByEl.set(el, set); }
    set.add(uid);
}

export function isElementAnimating(el) {
    if (!el) return false;
    const set = _animsByEl.get(el);
    if (!set) return false;
    const registry = window.oscillaAnimRegistry || {};
    for (const uid of set) {
        const entry = registry[uid];
        if (!entry || entry.el !== el) { set.delete(uid); continue; }
        if (entry.started === true) return true;
        if (window.runningAnimations?.has?.(uid)) return true;
    }
    return false;
}

export function resetAllAnimations() {
    stopSequence();   // pending next:/tdelay steps and a moving zoom() end with a reset
    window.pendingCueStarts?.clear();   // their timers went with the sequence
    window.oscillaZoom?.restorePlayheadLine?.();   // a zoom may have moved the playhead line off screen
    const registry = window.oscillaAnimRegistry || {};
    let n = 0;
    for (const uid in registry) {
        const entry = registry[uid];
        const el = entry?.el;
        const cfg = entry?.cfg;
        if (!el) continue;
        // Stopped, not paused: a paused loop stays in the coordinator and
        // holds its phase, so it would sit mid-turn with its wrapper turned.
        try { cfg?._anim?.stop?.(); } catch { /* ignore */ }
        try { cfg?._anim?.pause?.(); } catch { /* ignore */ }
        if (cfg?.kind === "trans" || entry.kind === "trans") {
            try { resetTrans(el, cfg || {}); } catch { /* ignore */ }
        }
        try { cfg?._reset?.(); } catch { /* ignore */ }
        for (const t of ["_oscillaRotateAnim", "_oscillaScaleAnim", "_oscillaColorAnim", "_oscillaTransTimer"]) {
            try { el[t]?.stop?.(); } catch { /* ignore */ }
            try { clearTimeout(el[t]); } catch { /* ignore */ }
        }
        try { clearAnimTransforms(el); } catch { /* ignore */ }
        entry._observerPaused = false;
        n++;
    }
    // draw() ink is a record of where the animations went; putting them back
    // to the start without wiping it would leave last time's lines on the page.
    try { window.oscillaDraw?.resetAllDrawings?.(); } catch { /* ignore */ }
    // What started when the score rendered starts again from its start, its
    // tdelay included. A motion a cue started is not replayed: the playhead
    // fires that cue again when it crosses it.
    for (const uid in registry) {
        const entry = registry[uid];
        if (!entry?.autoStart || !entry.el?.isConnected) continue;
        try { entry.autoStart(); } catch (err) { console.warn(`[oscillaAnim] restart of "${uid}" failed:`, err); }
        if (window.oscillaAnimFreeze?.active) queueMicrotask(() => window.oscillaAnimFreeze?.holdEntry(uid));
    }
    if (window.refreshObserver) window.refreshObserver();
    return n;
}

// ------------------------------------------------------------
// autoStartAnimation(uid, start, fromCueTrigger)
// ------------------------------------------------------------
// Runs start() for a motion that starts when the score renders (trig:auto,
// or any trig in page mode) and keeps it on the registry entry, so
// resetAllAnimations can start it again. start is the whole start, tdelay
// included.
export function autoStartAnimation(uid, start, fromCueTrigger = false) {
    const entry = window.oscillaAnimRegistry?.[uid];
    if (entry) entry.autoStart = fromCueTrigger ? null : start;
    start();
}

document.addEventListener("DOMContentLoaded", () => {
    document.getElementById("anim-reset-all")?.addEventListener("click", resetAllAnimations);
});

// ------------------------------------------------------------
// registerAnimation()
// ------------------------------------------------------------
export function registerAnimation(el, kind, cfg, startFn) {
    if (el.dataset) el.dataset.animUid = cfg.uid;

    // Only warn if the uid is being claimed by a DIFFERENT element.
    // Re-triggering the same animation (playhead scrubbed back across
    // the cue, etc.) is a normal flow that hits this path repeatedly.
    const existing = window.oscillaAnimRegistry[cfg.uid];
    if (existing && existing.el !== el) {
        console.warn(`[oscillaAnim] ⚠️ Duplicate UID "${cfg.uid}" — claimed by a different element`);
    }

    const trig = cfg.trig || "auto";

    // Offscreen-pause opt-out. observer:0 in the DSL keeps the animation
    // running offscreen (for animations whose steps drive audio/OSC that
    // must keep sounding). When the param is absent, OSC-sending
    // animations are exempt automatically; everything else is managed.
    const args = cfg.astArgs || [];
    const argVal = (k) => args.find(a => (a.key || a.type) === k)?.value;
    const obsVal = argVal('observer') ?? cfg.observer;
    let keepAlive;
    if (obsVal !== undefined) {
        keepAlive = ['0', 'off', 'false', 'no'].includes(String(obsVal).toLowerCase());
    } else {
        keepAlive = Number(argVal('osc') ?? cfg.osc) > 0;
    }

    _indexAnimation(el, cfg.uid);
    // The transform wrapper is made NOW, at registration, not when the
    // animation first runs. Made at trigger time it is a DOM insertion per
    // member in the middle of playback, each forcing a layout -- and, with
    // the overview strip showing the score through <use>, a rebuild of the
    // layer's whole instance tree. With the wrapper already in place a
    // trigger only writes styles.
    if (kind !== "trans" && el instanceof SVGElement && !el._oscillaAnimWrapper) {
        try { ensureAnimWrapper(el); } catch { /* an element that cannot be wrapped just gets one later */ }
    }
    window.oscillaAnimRegistry[cfg.uid] = {
        el,
        kind,
        cfg,
        trig,
        startFn,

        started: (trig === "playhead"),
        uid: cfg.uid,
        keepAlive,
        registeredAt: performance.now(),
        forceVisible: window.isPageOverlay === true
    };

    if (window.refreshObserver) window.refreshObserver();

    // Apply any pending server sync state for this animation
    _applyPendingSnapshotForUid(cfg.uid);
    // "Freeze all animations" is on: whatever starts now is held with the
    // rest. The cue modules call start() synchronously right after
    // registering, so a microtask runs after that start and pauses it before
    // a frame is drawn (dslInspector.js oscillaAnimFreeze).
    if (window.oscillaAnimFreeze?.active) queueMicrotask(() => window.oscillaAnimFreeze?.holdEntry(cfg.uid));
}

// ------------------------------------------------------------
// registerRunningAnimation(uid, instance)
// ------------------------------------------------------------
export function registerRunningAnimation(uid, instance) {
    if (!uid) return;

    // store instance in Map
    window.runningAnimations.set(uid, instance);

    // used by visibility system
    instance.wasPaused = false;
}

// ------------------------------------------------------------
// clearRunningAnimation(uid)
// ------------------------------------------------------------
export function clearRunningAnimation(uid) {
    if (!uid) return;
    window.runningAnimations.delete(uid);
}
